import { Item } from 'services/items';
import {
  AutoPurchaseOrderItem,
  createPurchaseOrder,
  getPurchaseOrderId,
  initialPurchaseOrder,
  initialPurchaseOrderItem,
  PurchaseOrder,
} from 'services/purchaseOrders';
import { Vendor } from 'services/vendors';
import _ from 'lodash';

const findInventory = (item: Item, locationId: number) => {
  const inventory = item.inventoryLocationSummaryList.find(
    (ils) => ils.rootLocationId === locationId
  );
  return inventory || null;
};

const findReorderPoint = (item: Item, locationId: number) => {
  const reorderPoint = item.reorderPointsList.find(
    (r) => r.locationId === locationId
  );
  return reorderPoint || null;
};

export const resolveQuantity = (
  autoOrderItem: AutoPurchaseOrderItem,
  locationId: number
) => {
  const item = autoOrderItem.item;
  const inventory = findInventory(item, locationId);
  const itemDefaultUom = item.defaultUom;
  const itemUomConversions = item.itemUomConversionList;
  const reorderPoint = findReorderPoint(item, locationId);
  const quantityToOrder =
    (reorderPoint?.max ?? 0) -
    (inventory?.totalQty ?? 0) +
    (inventory?.allocatedOnPurchaseOrders ?? 0) +
    (inventory?.allocatedOnSalesOrders ?? 0) -
    (inventory?.qtyIncomingOnPurchaseOrders ?? 0) -
    (inventory?.qtyIncomingOnSalesOrders ?? 0);

  if (itemDefaultUom?.id && autoOrderItem.uomId === itemDefaultUom.id) {
    return quantityToOrder > 0 ? Math.ceil(quantityToOrder) : 0;
  }

  const itemUOMConversionFrom = itemUomConversions?.find(
    (u) =>
      u.fromUomId === autoOrderItem.uomId && u.toUomId === itemDefaultUom?.id
  );
  const itemUOMConversionTo = itemUomConversions?.find(
    (u) =>
      u.toUomId === autoOrderItem.uomId && u.fromUomId === itemDefaultUom?.id
  );

  const factorMultiply = itemUOMConversionTo
    ? _.get(itemUOMConversionTo, 'factor', 1)!
    : null;
  const factorDivide = itemUOMConversionFrom
    ? _.get(itemUOMConversionFrom, 'factor', 1)!
    : null;

  const factor = factorDivide ? 1 / factorDivide : factorMultiply ?? 1;

  return Math.ceil(quantityToOrder * factor) || 0;
};
const shouldIncludeInAutoOrder = (item: Item, locationId: number) => {
  const inventory = findInventory(item, locationId);
  const reorderPoint = findReorderPoint(item, locationId);

  const onHand = inventory?.totalQty ?? 0;
  const incomingPO = inventory?.qtyIncomingOnPurchaseOrders ?? 0;
  const incomingSO = inventory?.qtyIncomingOnSalesOrders ?? 0;

  const totalStock = onHand + incomingPO + incomingSO;
  const min = reorderPoint?.min ?? 0;

  // Only include if total stock is below the min threshold
  return totalStock < min;
};

export const filterReorderPointsAndLocations = (
  autoItems: AutoPurchaseOrderItem[],
  locationId: number
) => {
  return autoItems
    .map((ai) => ({
      ...ai,
      item: {
        ...ai.item,
        reorderPointsList: ai.item.reorderPointsList.filter(
          (r) => r.locationId === locationId
        ),
        inventoryLocationSummaryList:
          ai.item.inventoryLocationSummaryList.filter(
            (ils) => ils.rootLocationId === locationId
          ),
      },
    }))
    .filter((ai) => shouldIncludeInAutoOrder(ai.item, locationId))
    .map((ai) => ({
      ...ai,
      quantity: resolveQuantity(ai, locationId),
    }));
};

export const createAutoPurchaseOrders = async (
  autoItems: AutoPurchaseOrderItem[],
  locationId: number,
  vendors: Vendor[]
) => {
  const newOrders: PurchaseOrder[] = [];

  for (const obj of autoItems) {
    const index = newOrders.findIndex((p) => p.vendorId === obj.vendorId);
    const vendor = vendors.find((v) => v.id === obj.vendorId) || null;
    const vendorItem =
      obj.item?.vendorItems?.find(
        (vi) => vi.vendorId === obj.vendorId && vi.defaultUomId === obj.uomId
      ) || null;
    if (index === -1) {
      const nextPoNumber = await getPurchaseOrderId();
      newOrders.push({
        ...initialPurchaseOrder,
        locationId: locationId,
        vendorId: obj.vendorId,
        exchangeCurrencyId: vendor?.currencyId ? vendor.currencyId : 1,
        vendor: vendor,
        number: nextPoNumber.toString(),
        purchaseOrderItemList: [
          {
            ...initialPurchaseOrderItem,
            item: obj.item,
            itemId: obj.item.id,
            unitCost: vendorItem?.cost ?? 0,
            quantity: obj.quantity,
            uom: obj.uom || null,
            uomId: obj.uomId ?? null,
          },
        ],
      });
    } else {
      newOrders[index].purchaseOrderItemList.push({
        ...initialPurchaseOrderItem,
        item: obj.item,
        itemId: obj.item.id,
        unitCost: vendorItem?.cost ?? 0,
        quantity: obj.quantity,
        uom: obj.uom || null,
        uomId: obj.uomId ?? null,
      });
    }
  }

  // set line numbers in po items
  const orderedPurchaseOrders = newOrders.map((o) => ({
    ...o,
    purchaseOrderItemList: o.purchaseOrderItemList.map((item, index) => ({
      ...item,
      lineNumber: index,
    })),
  }));

  return orderedPurchaseOrders;
};

export const createPurchaseOrders = async (purchaseOrders: PurchaseOrder[]) => {
  for (const po of purchaseOrders) {
    await createPurchaseOrder(po);
  }
};

export const createPOsAndReturnIds = async (
  purchaseOrders: PurchaseOrder[]
) => {
  const createdPOs = await Promise.all(
    purchaseOrders.map(
      async (purchaseOrder: PurchaseOrder) =>
        await createPurchaseOrder(purchaseOrder)
    )
  );

  return createdPOs.map((po) => po.id);
};
